/* suPer shop script. Loaded on every page that has no 3D (product, collection, cart, search, blog, pages); the 3D bundle is not.
   Everything here is progressive enhancement: the product form, the cart form and the links all work without it.
     - mobile menu (same behaviour as src/shell/shell.js, the shell bundle of the 3D pages, which these pages do not load. Both set and check toggle.dataset.enhanced,
       so a page that ever loaded both would still bind the menu once; keep the two copies in sync)
     - header cart count (aria-label "Cart, N items"), refreshed after add to cart and when a page returns from the back-forward cache
     - product page: variant chips without a reload, quantity buttons, gallery thumbnails, add to cart through /cart/add.js
     - cart page: quantity buttons and remove through /cart/change.js, then the section is re-rendered with the Section Rendering API
   No dependencies. Every network failure falls back to the normal form post or a reload. */
(function () {
  'use strict';
  var doc = document;
  doc.documentElement.classList.add('js-ready');   // the page head adds .reveal-all after 5 s when this never happens

  var JSON_HEADERS = { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' };

  function all(sel, root) { return Array.prototype.slice.call((root || doc).querySelectorAll(sel)); }
  function one(sel, root) { return (root || doc).querySelector(sel); }
  function readJson(res) { return res.json().then(function (json) { return { ok: res.ok, status: res.status, json: json }; }, function () { return { ok: res.ok, status: res.status, json: null }; }); }
  function plainSubmit(form) { HTMLFormElement.prototype.submit.call(form); }   // no 'submit' event, and immune to a control named "submit"

  /* ---------- mobile menu ---------- */
  function initNav() {
    var toggle = one('[data-nav-toggle]');
    var panel = one('[data-mobile-nav]');
    if (!toggle || !panel || toggle.dataset.enhanced) return;
    toggle.dataset.enhanced = '1';
    var behind = all('main, .announce, .site-footer');   // the page behind an open full-screen menu is inert
    function setOpen(open) {
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      panel.classList.toggle('is-open', open);
      behind.forEach(function (el) { if (open) el.setAttribute('inert', ''); else el.removeAttribute('inert'); });
    }
    toggle.addEventListener('click', function () { setOpen(toggle.getAttribute('aria-expanded') !== 'true'); });
    doc.addEventListener('keydown', function (e) { if (e.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') { setOpen(false); toggle.focus(); } });
    panel.addEventListener('click', function (e) { if (e.target.closest && e.target.closest('a')) setOpen(false); });
    if (window.matchMedia) window.matchMedia('(min-width: 901px)').addEventListener('change', function (m) { if (m.matches) setOpen(false); });
  }

  /* ---------- header cart count ---------- */
  function setCartCount(n) {
    n = Math.max(0, parseInt(n, 10) || 0);
    all('[data-cart-link]').forEach(function (a) {
      var count = one('[data-cart-count]', a);
      if (count) count.textContent = String(n);
      a.setAttribute('aria-label', (a.dataset.label || 'Cart') + ', ' + n + ' ' + (n === 1 ? (a.dataset.one || 'item') : (a.dataset.other || 'items')));
    });
  }
  function refreshCartCount() {
    var link = one('[data-cart-link]');
    var url = (link && link.dataset.cartJsUrl) || '/cart.js';
    return fetch(url, { headers: JSON_HEADERS, credentials: 'same-origin' }).then(readJson).then(function (r) {
      if (r.ok && r.json && typeof r.json.item_count === 'number') { setCartCount(r.json.item_count); return r.json; }
      return null;
    }).catch(function () { return null; });
  }
  window.addEventListener('pageshow', function (e) { if (e.persisted) refreshCartCount(); });

  /* ---------- quantity stepper (shared by the product page and the cart) ---------- */
  function clampQty(input, value) {
    var min = input.min === '' ? 1 : parseInt(input.min, 10);
    var max = input.max === '' ? Infinity : parseInt(input.max, 10);
    value = parseInt(value, 10);
    if (isNaN(value)) value = isNaN(min) ? 1 : min;
    return Math.min(max, Math.max(isNaN(min) ? 1 : min, value));
  }

  /* ---------- product page ---------- */
  function initProduct(root) {
    if (root.dataset.enhanced) return;
    root.dataset.enhanced = '1';
    var jsonEl = one('[data-product-json]', root);
    var form = one('form.product-form', root);
    if (!jsonEl || !form) return;
    var variants = [];
    try { variants = JSON.parse(jsonEl.textContent).variants || []; } catch (e) { return; }
    var byId = {};
    variants.forEach(function (v) { byId[String(v.id)] = v; });

    var ds = root.dataset;
    var priceEl = one('[data-price]', root), compareEl = one('[data-compare]', root), compareLabel = one('[data-compare-label]', root);
    var statusEl = one('[data-status]', root), noteEl = one('[data-preorder-note]', root);
    var addBtn = one('[data-add-button]', form), addLabel = one('[data-add-label]', form);
    var msgEl = one('[data-message]', form), errEl = one('[data-error]', form);
    var qty = one('[data-qty-input]', form);
    var select = one('[data-variant-select]', form);
    var radiosId = all('input[type="radio"][name="id"]', form);
    var optionSets = all('[data-options="multi"]', form);
    var mediaItems = all('[data-media-id]', root), thumbs = all('[data-thumb]', root);
    var busy = false;
    var lastStatus = statusEl ? statusEl.textContent.trim() : '';

    function chosenVariant() {
      if (radiosId.length) {
        var c = radiosId.filter(function (r) { return r.checked; })[0];
        return c ? byId[c.value] : null;
      }
      if (optionSets.length) {
        var picked = optionSets.map(function (fs) { var r = one('input:checked', fs); return r ? r.value : null; });
        return variants.filter(function (v) { return v.options.every(function (o, i) { return o === picked[i]; }); })[0] || null;
      }
      var hidden = one('input[name="id"]', form);
      return hidden ? byId[hidden.value] || null : (variants[0] || null);
    }

    function showMedia(id) {
      if (!mediaItems.length || id == null) return;
      var key = String(id), found = false;
      mediaItems.forEach(function (li) { if (li.dataset.mediaId === key) found = true; });
      if (!found) return;
      mediaItems.forEach(function (li) { if (li.dataset.mediaId === key) li.setAttribute('data-active', ''); else li.removeAttribute('data-active'); });
      thumbs.forEach(function (b) { b.setAttribute('aria-pressed', b.dataset.thumb === key ? 'true' : 'false'); });
    }
    thumbs.forEach(function (b) { b.addEventListener('click', function () { showMedia(b.dataset.thumb); }); });

    function setMessage(el, text) { if (el) el.textContent = text || ''; }

    function render(v, userChange) {
      var text = '';
      if (!v) {
        text = ds.textStatusUnavailable || '';
        addBtn.disabled = true;
        if (addLabel) addLabel.textContent = ds.labelUnavailable || '';
        if (noteEl) noteEl.hidden = true;
      } else {
        if (priceEl) priceEl.innerHTML = v.price;
        var sale = !!v.compare;
        if (compareEl) { compareEl.hidden = !sale; if (sale) compareEl.innerHTML = v.compare; }
        if (compareLabel) compareLabel.hidden = !sale;
        if (!v.available) text = ds.textStatusSoldout || '';
        else if (v.preorder) text = ds.textStatusPreorder || '';
        addBtn.disabled = !v.available;
        if (addLabel) addLabel.textContent = !v.available ? (ds.labelSoldout || '') : (v.preorder ? (ds.labelPreorder || '') : (ds.labelAdd || ''));
        if (noteEl) noteEl.hidden = !(v.preorder && v.available);
        if (qty) {
          if (v.max) qty.max = String(v.max); else qty.removeAttribute('max');
          qty.value = String(clampQty(qty, qty.value));
        }
        showMedia(v.media);
        if (select) select.value = String(v.id);
        if (userChange && window.history && history.replaceState) {   // shareable link to the chosen size; the page URL is left alone until the visitor picks
          try { var u = new URL(window.location.href); u.searchParams.set('variant', String(v.id)); history.replaceState(history.state, '', u.toString()); } catch (e) { /* keep the current URL */ }
        }
      }
      if (statusEl) { statusEl.textContent = text; statusEl.hidden = !text; }
      setMessage(errEl, '');
      if (userChange) setMessage(msgEl, text !== lastStatus ? text : '');   // announce a change of availability, not every size click
      lastStatus = text;
    }

    form.addEventListener('change', function (e) {
      var t = e.target;
      if (t === qty) { qty.value = String(clampQty(qty, qty.value)); return; }
      if (t.matches && t.matches('input[type="radio"]')) render(chosenVariant(), true);
    });

    var minus = one('[data-qty-minus]', form), plus = one('[data-qty-plus]', form);
    if (qty && minus) minus.addEventListener('click', function () { qty.value = String(clampQty(qty, (parseInt(qty.value, 10) || 1) - 1)); setMessage(msgEl, ''); });
    if (qty && plus) plus.addEventListener('click', function () {
      var v = chosenVariant(); var before = parseInt(qty.value, 10) || 1; var next = clampQty(qty, before + 1);
      qty.value = String(next);
      setMessage(msgEl, (v && v.max && next === before) ? (ds.textMax || '') : '');
    });

    function setBusy(on) {
      busy = on;
      if (on) { addBtn.setAttribute('aria-busy', 'true'); addBtn.setAttribute('aria-disabled', 'true'); } else { addBtn.removeAttribute('aria-busy'); addBtn.removeAttribute('aria-disabled'); }
    }

    form.addEventListener('submit', function (e) {
      var v = chosenVariant();
      if (!window.fetch || !window.FormData) return;          // old browser: normal post
      e.preventDefault();
      if (busy || !v || !v.available) return;
      setBusy(true); setMessage(errEl, ''); setMessage(msgEl, '');
      fetch(ds.addUrl, { method: 'POST', body: new FormData(form), headers: JSON_HEADERS, credentials: 'same-origin' })
        .then(readJson)
        .then(function (r) {
          setBusy(false);
          if (r.ok) {
            msgEl.textContent = (ds.textAdded || '') + ' ';
            var a = doc.createElement('a'); a.href = ds.cartUrl || '/cart'; a.textContent = ds.textViewCart || ''; msgEl.appendChild(a);
            refreshCartCount();
          } else if (r.json && (r.json.description || r.json.message)) {
            setMessage(errEl, String(r.json.description || r.json.message));
          } else { plainSubmit(form); }
        })
        .catch(function () { setBusy(false); plainSubmit(form); });
    });

    render(chosenVariant(), false);   // sync the page with the chosen chip (the server already did; this also wires ?variant= edge cases)
  }

  /* ---------- cart page ---------- */
  function initCart(root) {
    if (root.dataset.enhanced) return;
    root.dataset.enhanced = '1';
    var ds = root.dataset;
    var statusEl = one('[data-cart-status]', root), errEl = one('[data-cart-error]', root);
    var pending = {}, running = false, off = false;   // off: the fetch path failed, let the forms post normally

    function body() { return one('[data-cart-body]', root); }
    function setBusy(on) { var b = body(); if (b) { if (on) b.setAttribute('aria-busy', 'true'); else b.removeAttribute('aria-busy'); } }

    function sectionHtml(json) {
      if (json && json.sections && json.sections[ds.sectionId]) return Promise.resolve(json.sections[ds.sectionId]);
      return fetch(window.location.pathname + '?sections=' + encodeURIComponent(ds.sectionId), { headers: JSON_HEADERS, credentials: 'same-origin' })
        .then(readJson).then(function (r) { return r.ok && r.json ? r.json[ds.sectionId] : null; });
    }
    function swap(html, focus) {
      var next = new DOMParser().parseFromString(html, 'text/html');
      var nextBody = one('[data-cart-body]', next);
      var cur = body();
      if (!nextBody || !cur) { window.location.reload(); return; }
      cur.innerHTML = nextBody.innerHTML;
      var target = null;
      if (focus && focus.key) { var line = all('[data-line]', cur).filter(function (l) { return l.dataset.key === focus.key; })[0]; if (line) target = one(focus.sel, line); }
      if (!target) target = one('.cart-empty a, [data-line-qty]', cur);
      if (target && target.focus) target.focus({ preventScroll: true });
    }
    function request(key, quantity) {
      return fetch(ds.changeUrl, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: key, quantity: quantity, sections: ds.sectionId, sections_url: window.location.pathname })
      }).then(readJson);
    }
    function failToForm() {
      off = true;
      var form = one('form.cart-form', root);
      var upd = form && one('button[name="update"]', form);
      if (form && form.requestSubmit && upd) form.requestSubmit(upd); else if (form) plainSubmit(form); else window.location.reload();
    }

    var lastFocus = null, removing = false;
    function run() {
      var keys = Object.keys(pending);
      if (!keys.length) { running = false; setBusy(false); return; }
      running = true; setBusy(true);
      var key = keys[0], quantity = pending[key];
      delete pending[key];
      request(key, quantity).then(function (r) {
        var more = Object.keys(pending).length > 0;
        if (!r.ok) {
          var message = r.json && (r.json.description || r.json.message);
          if (!message) throw new Error('change failed');
          setErr(String(message));
          return sectionHtml(null).then(function (html) { if (html && !more) swap(html, lastFocus); });
        }
        setErr('');
        if (r.json && typeof r.json.item_count === 'number') setCartCount(r.json.item_count);
        if (more) return null;
        return sectionHtml(r.json).then(function (html) {
          if (!html) throw new Error('no section');
          swap(html, lastFocus);
          if (statusEl) statusEl.textContent = (removing ? ds.textRemoved : ds.textUpdated) || '';
          removing = false;
        });
      }).then(run, function () { pending = {}; running = false; setBusy(false); setErr(ds.textError || ''); failToForm(); });
    }
    function setErr(text) { if (errEl) errEl.textContent = text || ''; }
    function schedule(key, quantity, focus) {
      pending[key] = quantity;
      if (focus) lastFocus = focus;
      removing = quantity === 0;
      if (!running) run();
    }

    function lineOf(el) { return el.closest ? el.closest('[data-line]') : null; }
    root.addEventListener('click', function (e) {
      if (off) return;
      var t = e.target.closest ? e.target.closest('[data-qty-minus],[data-qty-plus],[data-remove]') : null;
      if (!t || !root.contains(t)) return;
      var line = lineOf(t); if (!line) return;
      var input = one('[data-line-qty]', line);
      if (t.hasAttribute('data-remove')) {
        e.preventDefault();
        if (!window.fetch) return;
        schedule(line.dataset.key, 0, { key: line.dataset.key, sel: '[data-line-qty]' });
        return;
      }
      if (!window.fetch) return;
      var current = parseInt(input.value, 10); if (isNaN(current)) current = 0;
      var next = Math.max(0, current + (t.hasAttribute('data-qty-plus') ? 1 : -1));
      input.value = String(next);                                   // optimistic: the next click builds on it
      schedule(line.dataset.key, next, { key: line.dataset.key, sel: t.hasAttribute('data-qty-plus') ? '[data-qty-plus]' : '[data-qty-minus]' });
    });
    root.addEventListener('change', function (e) {
      var input = e.target;
      if (off || !input.matches || !input.matches('[data-line-qty]') || !window.fetch) return;
      var line = lineOf(input); if (!line) return;
      var value = parseInt(input.value, 10); if (isNaN(value) || value < 0) value = parseInt(input.dataset.initial, 10) || 0;
      input.value = String(value);
      schedule(line.dataset.key, value, { key: line.dataset.key, sel: '[data-line-qty]' });
    });
    root.addEventListener('submit', function (e) {
      // Enter inside a quantity field: apply that line through the fetch path; the Checkout button submits normally
      var sub = e.submitter;
      if (off || (sub && sub.name === 'checkout') || !window.fetch) return;
      e.preventDefault();
      var active = doc.activeElement;
      if (active && active.matches && active.matches('[data-line-qty]')) active.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  function initAll(scope) {
    initNav();
    all('[data-product]', scope).forEach(initProduct);
    all('[data-cart]', scope).forEach(initCart);
  }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', function () { initAll(doc); }); else initAll(doc);
  doc.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
